/**
 * Marketing footer content that is NOT link data — the trust badges and the
 * copyright / built-by lines.
 *
 * The footer's grouped link columns moved to the shared chrome SOT
 * (`@/components/chrome` → `FOOTER_LINK_GROUPS.site`) so the site footer and the
 * dark landing footer draw every route href from one place and can no longer
 * drift apart. `TRUST_BADGES` is now the same kind of SOT for the badges:
 * **both** footers render it.
 *
 * Why that matters: the dark landing footer used to hardcode
 * `SOC 2 Type II · in progress · Q3 2026` directly in JSX. That was an
 * **overclaim** — the public commitment is **Type I** (`compliance-status.json`
 * → `soc2_type_i`, and the string below), the class of finding filed as SOC-F07
 * in `docs/compliance/SOC-REVIEW-2026-07-20.md` — and it was unlocalized and
 * un-updatable without a code change. One SOT, two consumers, no drift.
 *
 * Visible copy is i18n-externalised: badge labels + the copyright/built-by keys
 * carry `*Key` fields that the `Footer` component resolves through `t('footer.*')`.
 */

export interface TrustBadge {
  /** i18n key for the badge label, resolved via `t('footer.*')`. */
  readonly labelKey: string;
  /**
   * Whether the badge describes something already in force (`ready`) or a
   * commitment still being worked towards (`in-progress`). The dark landing
   * footer tints its status dot from this; the light site footer renders plain
   * text and ignores it. Keep it honest — an `in-progress` badge must never be
   * presented as achieved.
   */
  readonly tone: 'ready' | 'in-progress';
}

export const TRUST_BADGES: readonly TrustBadge[] = [
  { labelKey: 'footer.badgeGdpr', tone: 'ready' },
  { labelKey: 'footer.badgeSoc2', tone: 'in-progress' },
  { labelKey: 'footer.badgeEuResidency', tone: 'ready' },
];

/** i18n key for the "Made in the EU" line, resolved via `t('footer.*')`. */
export const BUILT_BY_LINE_KEY = 'footer.builtBy';

/** i18n key for the copyright line; carries a `{year}` token. */
export const COPYRIGHT_KEY = 'footer.copyright';
